import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setError(null);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Register with email/password
  const register = async (email, password, name, phone) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name,
        phone: phone || null
      }, { withCredentials: true });
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Login with email/password
  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { withCredentials: true });
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  // Google OAuth login
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Process Google OAuth callback
  const processGoogleCallback = async (sessionId) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/google/session`, {
        session_id: sessionId
      }, { withCredentials: true });
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (err) {
      const message = err.response?.data?.detail || 'Google login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Request password reset
  const requestPasswordReset = async (email) => {
    try {
      setError(null);
      await axios.post(`${API}/auth/password-reset/request`, { email });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send reset email';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Confirm password reset
  const confirmPasswordReset = async (token, newPassword) => {
    try {
      setError(null);
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      setError(null);
      const response = await axios.put(`${API}/user/profile`, data, {
        withCredentials: true
      });
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update profile';
      setError(message);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    loginWithGoogle,
    processGoogleCallback,
    requestPasswordReset,
    confirmPasswordReset,
    changePassword,
    updateProfile,
    checkAuth,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
